// ============================================================
// CarePeopleReconcileCard.js — People-17 (2026-10-01)
// Links every active staff login (manager, admin, carer) of this client to
// its CarePeople HR profile, and creates a draft profile for anyone who has
// none. Preview first: the dry run writes nothing and lists who would be
// changed; Apply runs it for real (audited) and sends the client's managers
// one summary notification if drafts were created.
//
// The same sweep runs every night at 02:00 as a safety net, so this card is
// for "do it now and show me", not the only way it happens.
// ============================================================

import React, { useState } from 'react';
import { toast } from '../../components/Toaster';
import { reconcileClientCarePeople } from '../../api/adminClientConfig';
import { translateError } from '../../utils/errorMessages';
import StatusMessage from '../../components/StatusMessage';

const ROLE_LABELS = { manager: 'Manager', admin: 'Admin', carer: 'Carer' };

// Wording for each outcome, before (preview) and after (applied).
const ACTION_TEXT = {
  reverse_linked: { preview: 'Will be linked to their existing HR profile', applied: 'Linked to their existing HR profile' },
  draft_created:  { preview: 'A draft HR profile will be created',          applied: 'Draft HR profile created' },
  ambiguous:      { preview: 'Left alone: two HR profiles share this email', applied: 'Left alone: two HR profiles share this email' },
  no_match:       { preview: 'Left alone: no HR profile matches',           applied: 'Left alone: no HR profile matches' },
  failed:         { preview: 'Could not be checked',                        applied: 'Failed' },
};

function plural(n, word) { return n === 1 ? word : `${word}s`; }

export function actionText(action, applied) {
  const t = ACTION_TEXT[action];
  if (!t) return applied ? 'Not changed' : 'No change';
  return applied ? t.applied : t.preview;
}

export default function CarePeopleReconcileCard({ clientId }) {
  const [result, setResult] = useState(null);   // last response
  const [busy, setBusy] = useState(null);       // 'preview' | 'apply' | null
  const [error, setError] = useState(null);

  async function run(dryRun) {
    setBusy(dryRun ? 'preview' : 'apply');
    setError(null);
    try {
      const res = await reconcileClientCarePeople(clientId, dryRun);
      setResult(res);
      if (!dryRun) {
        const done = (res.linked || 0) + (res.created || 0);
        toast.success(done === 0 ? 'Nothing needed changing' : `${done} ${done === 1 ? 'person' : 'people'} updated`);
      }
    } catch (err) {
      setError(translateError(err));
    } finally {
      setBusy(null);
    }
  }

  const applied = result && result.dryRun === false;
  const pending = result && result.dryRun === true ? (result.linked || 0) + (result.created || 0) : 0;

  return (
    <div className="pc-reconcile">
      <p className="pc-explain">
        Every manager, admin and carer who signs in to CareHub should have a CarePeople HR profile, or they
        will be missing from the org chart and cannot approve leave or supervisions. This links each sign-in
        to its HR profile by email, and creates a draft profile for anyone who has none. Preview shows who
        would change and writes nothing. This also runs every night.
      </p>

      <div className="pc-actions">
        <button type="button" className="pc-btn-secondary" onClick={() => run(true)} disabled={busy !== null}>
          {busy === 'preview' ? 'Checking…' : 'Preview'}
        </button>
        <button
          type="button"
          className="pc-btn-primary"
          onClick={() => run(false)}
          disabled={busy !== null || !result || result.dryRun !== true || pending === 0}
        >
          {busy === 'apply' ? 'Applying…' : 'Apply'}
        </button>
      </div>

      {error && <StatusMessage as="p" variant="error" className="pc-state pc-state--error">{error}</StatusMessage>}

      {result && (
        <div className="pc-reconcile-result">
          <StatusMessage as="p" variant="empty" className="pc-state">
            {applied ? 'Applied. ' : 'Preview, nothing written yet. '}
            {result.total} staff {result.total === 1 ? 'login' : 'logins'}: {result.alreadyLinked} already linked,{' '}
            {result.linked} {applied ? 'linked' : 'to link'}, {result.created} {plural(result.created, 'draft')} {applied ? 'created' : 'to create'}
            {result.skipped ? `, ${result.skipped} left alone` : ''}
            {result.failures ? `, ${result.failures} failed` : ''}.
          </StatusMessage>

          {result.people && result.people.length > 0 && (
            <table className="pc-table">
              <thead>
                <tr><th>Name</th><th>Email</th><th>Role</th><th>Outcome</th></tr>
              </thead>
              <tbody>
                {result.people.map((p) => (
                  <tr key={p.clientUserId}>
                    <td>{p.displayName || '—'}</td>
                    <td>{p.email || '—'}</td>
                    <td>{ROLE_LABELS[p.roleKey] || p.roleKey || '—'}</td>
                    <td>{actionText(p.action, applied)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
